import { type ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isAssistantLogoutInProgress } from "@/auth/assistantAuth";
import { Button } from "@/components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import useCurrentUserRoles from "@/features/users/hooks/useCurrentUserRoles";
import { hasAnyRole } from "@/auth/roles";
import AccessDeniedPage from "@/pages/AccessDeniedPage";

type ProtectedRouteProps = {
  children: ReactElement;
  requiredRoles?: readonly string[];
};

/**
 * Loading fallback for authentication checks.
 * CRITICAL: Must render actual UI, not null, to prevent frozen UI during auth.
 */
function AuthLoadingFallback({ message = "Verifying authentication..." }: { message?: string }) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-primary border-r-transparent"></div>
        <p className="mt-4 text-sm text-foreground-muted-light dark:text-foreground-muted-dark">{message}</p>
      </div>
    </div>
  );
}

function AccessCheckErrorFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">We couldn't verify your access</h1>
        <p className="mt-2 text-sm text-foreground-muted-light dark:text-foreground-muted-dark">
          Please refresh and try again. If the problem persists, contact support.
        </p>
        <Button type="button" className="mt-4" onClick={() => window.location.reload()}>
          Refresh page
        </Button>
      </div>
    </div>
  );
}

const ProtectedRoute = ({ children, requiredRoles }: ProtectedRouteProps): ReactElement => {
  const { authInitialized, isAuthenticated, isLoading } = useAuth();
  const { error: rolesError, loading: rolesLoading, roles } = useCurrentUserRoles({ enabled: requiredRoles != null });
  const location = useLocation();

  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  const loginPageTarget = `/login?redirectTo=${encodeURIComponent(returnTo)}`;
  const isLogoutInProgress = isAssistantLogoutInProgress();

  const isAuthCheckPending = isLoading || !authInitialized;
  const shouldRedirectToLoginPage = !isAuthCheckPending && !isAuthenticated && !isLogoutInProgress;

  // CRITICAL: Wait for auth to be initialized OR show loading during auth check
  // This prevents the flash of login screen while we verify cached tokens
  if (isAuthCheckPending) {
    return <AuthLoadingFallback />;
  }

  if (!isAuthenticated && isLogoutInProgress) {
    return <Navigate to="/logout/callback" replace />;
  }

  if (shouldRedirectToLoginPage) {
    return <Navigate to={loginPageTarget} replace />;
  }

  if (requiredRoles != null) {
    if (rolesLoading) {
      return <AuthLoadingFallback message="Checking access..." />;
    }

    if (rolesError) {
      return <AccessCheckErrorFallback />;
    }

    if (!hasAnyRole(roles, requiredRoles)) {
      return <AccessDeniedPage />;
    }
  }

  return children;
};

export default ProtectedRoute;
